import { cn } from "@/components/ui/utils";

export type OS = "mac" | "windows" | "linux" | "unknown";

function getOS(): OS {
  const platform = navigator.platform.toLowerCase();
  if (platform.includes("mac")) return "mac";
  if (platform.includes("win")) return "windows";
  if (platform.includes("linux")) return "linux";
  return "unknown";
}

/** The space bar, as a key's symbol: drawn on the line's foot, it is raised to sit in its key as the others do. */
const SPACE = "␣";

function formatKeyName(key: string, isMac: boolean): string {
  const keyLower = key.toLowerCase().trim();

  // Modifier keys
  if (keyLower === "meta" || keyLower === "cmd" || keyLower === "command") {
    return isMac ? "⌘" : "Ctrl";
  }
  if (keyLower === "ctrl" || keyLower === "control") {
    return "Ctrl";
  }
  if (keyLower === "alt") {
    return isMac ? "⌥" : "Alt";
  }
  if (keyLower === "shift") {
    return isMac ? "⇧" : "Shift";
  }

  // Special keys
  if (keyLower === "enter" || keyLower === "return") {
    return "↵";
  }
  if (keyLower === "escape" || keyLower === "esc") {
    return "Esc";
  }
  if (keyLower === "backspace") {
    return "⌫";
  }
  if (keyLower === "delete") {
    return isMac ? "⌦" : "Del";
  }
  if (keyLower === "tab") {
    return "⇥";
  }
  if (keyLower === "space" || keyLower === " ") {
    return SPACE;
  }
  if (keyLower.startsWith("arrow")) {
    if (keyLower === "arrowup") return "↑";
    if (keyLower === "arrowdown") return "↓";
    if (keyLower === "arrowleft") return "←";
    if (keyLower === "arrowright") return "→";
    return (
      key.replace("arrow", "").charAt(0).toUpperCase() +
      key.replace("arrow", "").slice(1)
    );
  }

  // Regular keys - capitalize first letter
  return key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
}

function formatHotkey(
  hotkey: string | Partial<Record<OS, string>>,
  os?: OS,
): string {
  const currentOS = os || getOS();
  const isMac = currentOS === "mac";

  // Handle OS-specific hotkeys
  let hotkeyString: string;
  if (typeof hotkey === "string") {
    hotkeyString = hotkey;
  } else {
    hotkeyString =
      hotkey[currentOS] || hotkey.mac || hotkey.windows || hotkey.linux || "";
  }

  if (!hotkeyString) return "";

  // Split by + and format each part
  const parts = hotkeyString
    .split("+")
    .map((part) => formatKeyName(part.trim(), isMac));

  // Join with appropriate separator
  if (isMac) {
    return parts.join("+");
  } else {
    return parts.join("+");
  }
}

export function HotkeyDecorator({
  hotkey,
  color = "mode",
  invertColor = false,
  size = "sm",
  variant = "outlined",
}: {
  hotkey: string | Partial<Record<OS, string>>;
  /**
   * ``inherit`` is the colour of the text it sits in: for a key drawn on a
   * solid button, whose text is light in one scheme and dark in the other.
   */
  color?: "mode" | "black" | "white" | "inherit";
  /** Invert the color (light ↔ dark). Useful when rendered inside a tooltip. */
  invertColor?: boolean;
  /**
   * "xs" is a key drawn inside a line of small text (a row's overline): the
   * text's own size and no taller than its line, so it does not hold the
   * line open.
   */
  size?: "sm" | "xs";
  /**
   * ``soft`` is a key on a ground of its colour, faint, with no edge: one
   * drawn on a soft button, where an edge reads as a box in a box.
   */
  variant?: "outlined" | "soft";
}) {
  const formatted = formatHotkey(hotkey);

  if (!formatted) return null;

  const isMac = getOS() === "mac";

  return (
    <div
      data-testid="hotkey-decorator"
      className={cn(
        // The colour its edge and ground are mixed from: its words', or -
        // inverted, in a tooltip - the other mode's.
        invertColor
          ? "[--hotkey-text:white] dark:[--hotkey-text:black]"
          : color === "mode"
            ? "[--hotkey-text:var(--foreground)]"
            : color === "inherit"
              ? "[--hotkey-text:currentColor]"
              : color === "black"
                ? "[--hotkey-text:black]"
                : "[--hotkey-text:white]",
        "flex items-center justify-center [border-width:1px] [border-style:solid] rounded pl-1 pr-1 pt-0 pb-0",
        variant === "soft"
          ? // The edge kept, unseen, so a soft key is the size of an
            // outlined one.
            "border-transparent bg-[color-mix(in_srgb,var(--hotkey-text)_10%,transparent)]"
          : "border-[color-mix(in_srgb,var(--hotkey-text)_10%,transparent)]",
        // Its typography's line, not one: the small key's a touch over one.
        size === "xs"
          ? "min-w-4 font-semibold text-[11px] [line-height:1.1] text-muted-foreground/80"
          : "min-w-5 font-medium text-xs leading-snug text-foreground",
        // The typography names a colour of its own; inheriting means taking
        // the one around it instead.
        color === "inherit" && "text-inherit",
        // A touch screen has no keys to press.
        "[@media(hover:_none)_and_(pointer:_coarse)]:hidden",
      )}
    >
      {isMac
        ? // Mac style: symbols without separators
          formatted.split("").map((char, i) => (
            <span
              key={i}
              className={cn(
                invertColor
                  ? "text-white dark:text-black"
                  : color === "mode"
                    ? "text-foreground"
                    : color === "inherit"
                      ? "text-[currentColor]"
                      : color === "black"
                        ? "text-black"
                        : "text-white",
                char === SPACE && "relative top-[-0.25em]",
              )}
            >
              {char}
            </span>
          ))
        : // Windows/Linux style: text with + separators
          formatted.split(SPACE).flatMap((part, i) =>
            i === 0
              ? [part]
              : [
                  <span key={i} className="relative top-[-0.25em]">
                    {SPACE}
                  </span>,
                  part,
                ],
          )}
    </div>
  );
}
